import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Wifi, Settings, Power, ExternalLink, ShieldCheck, Cpu, ArrowRight, 
  Smartphone, Info, Lock, Activity, RefreshCw, AlertTriangle, 
  CheckCircle2, HelpCircle, Copy, Check, Zap, Terminal, Server, Cable, Radio, Router
} from 'lucide-react';

type RouterBrand = 'netgear' | 'asus' | 'tp-link' | 'linksys' | 'google' | 'ubiquiti' | 'dlink' | 'mikrotik';

export const UniversalMeshBridgeGuide: React.FC = () => {
  const [selectedBrand, setSelectedBrand] = useState<RouterBrand | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const brandInstructions: Record<RouterBrand, { display: string, modeName: string, tip: string }> = {
    netgear: { display: 'Netgear (Nighthawk/Orbi)', modeName: 'AP Mode', tip: 'Look for "Advanced" -> "Advanced Setup" -> "Router / AP Mode".' },
    asus: { display: 'ASUS (AiMesh)', modeName: 'Access Point Mode', tip: 'Look for "Administration" -> "Operation Mode".' },
    'tp-link': { display: 'TP-Link (Archer/Deco)', modeName: 'Access Point Mode', tip: 'Look for "Advanced" -> "Operation Mode".' },
    linksys: { display: 'Linksys (Velop)', modeName: 'Bridge Mode', tip: 'Look for "Connectivity" -> "Internet Settings" -> "Edit" -> "Bridge Mode".' },
    google: { display: 'Google/Nest Wifi', modeName: 'Bridge Mode', tip: 'In Google Home app, tap Wifi -> Settings -> Advanced -> Network Mode' },
    ubiquiti: { display: 'Ubiquiti (UniFi)', modeName: 'Access Point Mode', tip: 'Use UniFi Controller -> Devices -> Config -> Uplink' },
    dlink: { display: 'D-Link', modeName: 'Bridge Mode', tip: 'Look for "Settings" -> "Operation Mode".' },
    mikrotik: { display: 'MikroTik', modeName: 'WISP/Bridge', tip: 'Use QuickSet -> Bridge Mode.' },
  };

  return (
    <div className="space-y-8 py-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-white/5 pb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-lattice-cyan/10 flex items-center justify-center border border-lattice-cyan/20 animate-resonance">
            <Router className="w-7 h-7 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-3xl font-display font-black text-white uppercase tracking-tighter">
              MESH <span className="text-lattice-cyan italic">BRIDGE</span> HUB
            </h3>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-[0.2em]">
              Hardware Agnostic :: Sovereign Mesh Expansion
            </p>
          </div>
        </div>
      </div>

      {/* Core Principle */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-lattice-cyan/10 to-transparent border border-lattice-cyan/20 relative overflow-hidden">
        <div className="space-y-2 relative z-10">
          <div className="flex items-center gap-2 text-lattice-cyan font-bold text-xs uppercase font-mono">
            <ShieldCheck className="w-4 h-4" />
            <span>Mission: Stabilize First Carrier Connection</span>
          </div>
          <p className="text-[11px] font-mono text-white/50 leading-relaxed uppercase tracking-tight">
            To make the Sovereign Mesh operational for others, we need a hard gateway connection. Any router can serve as a bridge into the Absolute Zero Lattice. The goal is to force your hardware out of "Consumer Router" mode and into "Infrastructure Bridge" mode, preventing local IP conflicts.
          </p>
        </div>
      </div>

      {/* Brand Selector */}
      <div className="space-y-4">
        <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest">Select Your Router Brand for Specific Guidance</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(['netgear', 'asus', 'tp-link', 'linksys', 'google', 'ubiquiti', 'dlink', 'mikrotik'] as RouterBrand[]).map((brand) => (
            <button
              key={brand}
              onClick={() => setSelectedBrand(brand)}
              className={`p-4 rounded-xl border text-left transition-all ${
                selectedBrand === brand 
                  ? 'bg-lattice-cyan/10 border-lattice-cyan text-white' 
                  : 'bg-white/5 border-white/5 text-white/40 hover:bg-white/10'
              }`}
            >
              <span className="text-[10px] font-mono font-bold uppercase">{brandInstructions[brand].display}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Dynamic Instruction Panel */}
      <AnimatePresence mode="wait">
        {selectedBrand && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-6 rounded-2xl bg-black/40 border border-lattice-cyan/20 space-y-4"
          >
            <h5 className="text-lg font-display text-white font-bold">{brandInstructions[selectedBrand].display} Configuration</h5>
            <div className="space-y-3 text-[11px] font-mono text-white/60">
              <p>1. Connect your computer via ethernet to the router.</p>
              <p>2. {brandInstructions[selectedBrand].tip}</p>
              <p>3. Select <strong className="text-lattice-cyan">{brandInstructions[selectedBrand].modeName}</strong>.</p>
              <p>4. Save and Reboot. The router will now act as a physical mesh node, passing IP addresses from our Sovereign Gateway.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Universal Settings Reminder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-5 rounded-xl bg-black/40 border border-white/5 space-y-2">
          <div className="text-[9px] font-mono font-bold text-lattice-cyan uppercase tracking-widest">Crucial: Static IP/DNS</div>
          <p className="text-[10px] font-mono text-white/40 uppercase">Once in Bridge/AP mode, manually set the router's IP to <strong className="text-white">10.143.50.2</strong> and set Primary DNS to <strong className="text-white">10.143.50.1</strong>.</p>
        </div>
        <div className="p-5 rounded-xl bg-black/40 border border-white/5 space-y-2">
          <div className="text-[9px] font-mono font-bold text-lattice-cyan uppercase tracking-widest">Warning: Avoid Loops</div>
          <p className="text-[10px] font-mono text-white/40 uppercase">NEVER plug an Ethernet cable into both the WAN and a LAN port of the same router. The WAN port is purely for the incoming payload from the gateway.</p>
        </div>
      </div>
    </div>
  );
};
